<template>
    <v-container>
        <loading-spinner v-if="loading"/>
        <v-row v-else-if="student">
            <v-col cols="12" md="8">
                <!-- BACK BTN AND COACH NAME -->
                <v-row>
                    <v-col cols="12" md="4">
                        <back-button :route="{name: 'company.admin', query: {coach: coachId}}"/>
                    </v-col>
                    <v-col cols="12" md="8" class="student-name">
                        {{ student.name }}
                    </v-col>
                </v-row>
                <!-- IMG AND DETAILS -->
                <v-row>
                    <v-col cols="12" md="4">
                        <div class="student-img" :style="`background-image: url(${studentImg})`"/>
                    </v-col>
                    <v-col cols="12" md="8">
                        <user-details-card :details="studentDetails"/>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12">
                        <equipments-profile :equipments="equipments"/>
                    </v-col>
                    <v-col cols="12">
                        <table-basic-info :items="itemsInfo" full-width/>
                    </v-col>
                    <v-col cols="12">
                        <wellness-chart class="mx-0" :docs="monthRoutines" />
                    </v-col>
                </v-row>
            </v-col>
            <v-col cols="12" md="4">
                <v-row>
                    <v-col cols="12" v-if="isCoach">
                        <recent-chat-profile :student="student" />
                    </v-col>
                    <v-col cols="12">
                        <div class="section-title">
                            {{ $t('common.next_routines') }}
                        </div>
                        <div v-if="nextRoutines.length === 0" class="no-next-routines">
                            {{ $t('common.no_next_routines') }}
                        </div>
                        <routine-date-card
                            v-for="routine in nextRoutines"
                            :key="routine.id"
                            :routine="routine"
                        />
                        <go-to v-if="isCoach" :name="$t('common.see_all')" :to="{name: 'coach.calendar', query: {studentId: studentId}}"/>
                    </v-col>
                    <v-col cols="12" align="right">
                        <confirm-button
                            color="error"
                            :label="$t('admin.unlink')"
                            :title="$t('admin.unlink_student')"
                            :content="$t('admin.unlink_student_text')"
                            alert-mode
                            :loading="loadingUnlink"
                            @confirm="confirmSubscriptionDelete"
                            ref="confirmUnlink"
                        />
                    </v-col>
                </v-row>
            </v-col>
        </v-row>

    </v-container>
</template>

<script>

import {db} from '@/utils/Firebase'
import {mapActions, mapState} from "vuex";
import KimsaConfirm from "@/components/common/KimsaConfirm";
import LoadingSpinner from "@/components/common/LoadingSpinner";
import AvatarProfile from "@/components/elements/profile/AvatarProfile";
import BasicInfoProfile from "@/components/elements/profile/BasicInfoProfile";
import EquipmentsProfile from "@/components/elements/profile/EquipmentsProfile";
import StudentStatistics from "@/components/student/StudentStatistics";
import WellnessChart from "@/components/statistics/WellnessChart";
import RecentChatProfile from "@/components/elements/profile/RecentChatProfile";
import RoutineDateCard from "@/components/common/cards/RoutineDateCard";
import BackButton from "@/components/common/BackButton";
import UserDetailsCard from "@/components/common/cards/UserDetailsCard";
import GoTo from "@/components/elements/buttons/goTo";
import TableBasicInfo from "@/components/elements/table/TableBasicInfo";
import ConfirmButton from "@/components/elements/buttons/ConfirmButton";

export default {
    components: {
        ConfirmButton,
        TableBasicInfo,
        GoTo,
        UserDetailsCard,
        BackButton,
        RoutineDateCard,
        RecentChatProfile,
        WellnessChart,
        StudentStatistics,
        EquipmentsProfile,
        BasicInfoProfile,
        AvatarProfile,
        LoadingSpinner,
        KimsaConfirm,
    },
    data(vm) {
        return {
            loading: false,
            student: null,
            monthRoutines: [],
            loadingUnlink: false,
            routinesWeekly: 0,
            routinesMonthly: 0,
            averageTime: 0,
        }
    },
    computed: {
        ...mapState(['equipment']),
        studentId(vm = this) {
            return vm.$route.params.id || null
        },
        studentImg(vm = this) {
            return vm.student?.avatar || vm.imgPlaceholder
        },
        equipments(vm = this) {
            if (!vm.student?.equipments) return []
            return vm.equipment?.filter(equipment => vm.student.equipments.includes(equipment.name)) || []
        },
        nextRoutines(vm = this) {
            return vm.monthRoutines
                ?.filter(routine => this.$moment(routine.rawDate.full).isSameOrAfter())
                ?.slice(0,5) || []
        },
        isCoach(vm = this) {
            return vm.account?.accountType === 'coach' || false
        },
        studentObjectives(vm = this) {
            return vm.student?.objectives?.join(',') || vm.$t('common.not_available_short')
        },
        age(vm = this) {
            if (!vm.student.birthDate) return vm.$t('common.not_available_short')
            return vm.$moment().diff(vm.$moment(vm.student.birthDate.seconds * 1000), 'years')
        },
        height(vm = this) {
            if (!vm.student.height) return vm.$t('common.not_available_short')
            return vm.student.height + ' ' + vm.$tc('common.cm', 1)
        },
        weight(vm = this) {
            if (!vm.student.weight) return vm.$t('common.not_available_short')
            return vm.student.weight + ' ' + vm.$tc('common.kg', 1)
        },
        studentDetails(vm = this) {
            if (!vm.student) return null
            return [
                {name: vm.$t('common.objectives'), value: vm.studentObjectives},
                {name: vm.$t('common.email'), value: vm.student.email},
                {name: vm.$t('common.age'), value: vm.age},
                {name: vm.$t('common.height'), value: vm.height},
                {name: vm.$t('common.weight'), value: vm.weight},
            ]
        },
        itemsInfo(vm = this) {
            return [
                {name: vm.$t('student.routinesMonthly'), value: vm.routinesMonthly},
                {name: vm.$t('student.routinesWeekly'), value: vm.routinesWeekly},
                {name: vm.$t('student.averageTime'), value: vm.averageTime},
            ]
        },
        coachId(vm = this) {
            if(vm.isCoach) return vm.currentUser.uid
            return vm.student?.studentSubscription?.coachId || null
        }
    },
    async mounted() {
        const vm = this
        await vm.loadStudent()
        await vm.loadMonthRoutines()
        if (vm.equipment.length === 0) await vm.bindEquipment()
    },
    methods: {
        ...mapActions(['bindEquipment']),
        async loadStudent() {
            const vm = this
            vm.loading = true
            let studentRef = await db.collection('users').doc(vm.studentId).get()
            vm.student = {id: vm.studentId, ...studentRef.data()}
            vm.loading = false
        },
        async loadMonthRoutines() {
            const vm = this

            let startOfMonth = vm.$moment().startOf('month').format('YYYY-MM-DD')
            let endOfMonth = vm.$moment().endOf('month').format('YYYY-MM-DD')
            let monthRoutinesRef = await db.collection('assignedRoutines')
                .where('studentId', '==', vm.studentId)
                .where('rawDate.full', ">=", startOfMonth)
                .where('rawDate.full', "<=", endOfMonth)
                .orderBy('rawDate.full', 'asc').get()

            vm.monthRoutines = monthRoutinesRef.docs.map(routine => {
                return {id: routine.id, ...routine.data()}
            })

            vm.loadStatistics()
        },
        loadStatistics() {
            const vm = this
            vm.routinesMonthly = vm.monthRoutines.length || 0
            vm.averageTime = 0
            let hours = 0
            let datesRoutineInMonth = []
            vm.monthRoutines.forEach(doc => {
                datesRoutineInMonth.push(vm.$moment(doc.rawDate.full).format('DD-MM-YYYY'))
                hours += Number(doc.advance?.postInfo?.duration || 0)
            })
            hours = Number(hours / 60).toFixed(2).replace('.00', '')
            vm.averageTime = Number(hours / vm.routinesMonthly).toFixed(2).replace('NaN', '0').replace('.00', '')

            let startOfWeek = vm.$moment().startOf('week');
            let datesOfWeek = [startOfWeek.format('DD-MM-YYYY')]
            for (let i = 1; i <= 6; i++) datesOfWeek.push(startOfWeek.add(1, 'days').format('DD-MM-YYYY'))
            vm.routinesWeekly = datesOfWeek.filter(val => datesRoutineInMonth.includes(val)).length || 0
        },
        async confirmSubscriptionDelete() {
            const vm = this
            vm.loadingUnlink = true

            let subscriptionId = vm.student?.studentSubscription?.id || null
            if(subscriptionId) await db.collection('subscriptions').doc(subscriptionId).delete()

            let payload = {studentSubscription: null}

            if(vm.student?.group) {
                payload.group = null
                let currentGroup = await db.collection('groups').doc(vm.student.group).get()
                let students = currentGroup.data().students.filter(st => st !== vm.studentId)
                await db.collection('groups').doc(vm.student.group).update({students: students})
            }
            if(vm.student?.team) {
                payload.team = null
                let currentTeamRef = await db.collection('teams').doc(vm.student.team.id).get()
                let currentTeam = {id: currentTeamRef.id, ...currentTeamRef.data()}
                let currentGroup = currentTeam?.groups?.find(group => vm.student.team.subgroupId === group.id)
                let students = currentGroup.students.filter(st => st !== vm.studentId)
                let groups = JSON.parse(JSON.stringify(currentTeam?.groups))
                let findGroupIndex = currentTeam?.groups?.findIndex(group => group.id === vm.student.team.subgroupId || null)
                groups[findGroupIndex].students = students
                await db.collection('teams').doc(vm.student.team.id).update({groups: groups})
            }

            await db.collection('users').doc(vm.studentId).update(payload)

            vm.loadingUnlink = false
            vm.$refs.deleteSubscriptionDialog.hide()
            vm.toast(vm.$t('coach.student_subscription_deleted'))

            await vm.$router.push({name: 'company.admin'})
        },
    },
}
</script>

<style scoped>

.section-title {
    font-weight: 700;
    font-size: 18px;
    line-height: 22px;
    color: #333333;
}

.no-next-routines {
    font-size: 14px;
    line-height: 17px;
    color: #333333;
    margin-top: 8px;
    text-align: center;
}

.b-underline {
    text-decoration: underline !important;
}

.student-name {
    font-weight: 600;
    font-size: 32px;
    line-height: 40px;
    text-align: center;
    color: #03342C;
}

.student-img {
    border-radius: 38px;
    height: 100%;
    min-height: 150px;
    background-color: rgba(51, 51, 51, .1);
    background-position: center;
    background-size: cover;
}

</style>
